<template>
  <div ref="journeyDateContainer">
    <label class="journey-date-select__label" for="pikaday">
      {{ label }}: &nbsp;
      <Tooltip
        class="journey-date-select__tooltip"
        :title="label"
        :text="tooltipText"
      />
    </label>
    <div class="journey-date-select">
      <div
        class="journey-date-select__input journey-date-select__input--date tgmag-input-group tgmag-input-group--label-fixed"
        :class="[
          { 'tgmag-input-group--invalid': !$v.date.required && $v.date.$dirty },
          { 'tgmag-input-group--valid': $v.date.required && $v.date.$dirty }
        ]"
      >
        <label :for="`tgmag-journey-date-select__date${uuid}`" v-if="date">{{
          placeholderDate
        }}</label>
        <vue-pikaday
          v-if="renderReady"
          readonly
          ref="pikaday"
          :id="`tgmag-journey-date-select__date${uuid}`"
          placeholder="Select date"
          v-model="date"
          :options="allPikadayOptions"
          @blur="$v.date.$touch()"
        />
      </div>
      <div
        class="journey-date-select__input journey-date-select__input--time tgmag-input-group tgmag-input-group--label-fixed"
        :class="[
          {
            'tgmag-input-group--invalid':
              ($v.time.$invalid && $v.time.$dirty) || !$v.date.isValidDateTime
          },
          {
            'tgmag-input-group--valid':
              !$v.time.$invalid && $v.time.$dirty && $v.date.isValidDateTime
          }
        ]"
      >
        <label :for="`tgmag-journey-date-select__time${uuid}`">{{
          placeholderTime
        }}</label>
        <input
          v-if="isTouch"
          ref="timepickerTouch"
          :id="`tgmag-journey-date-select__time${uuid}`"
          :type="showInput"
          v-model="time"
          :placeholder="placeholderTime"
          min="00:00"
          max="23:59"
          step="300"
          autocomplete="false"
          @input="update('time')"
          @blur="$v.time.$touch()"
        />
        <time-picker
          v-else
          ref="timepicker"
          :id="`tgmag-journey-date-select__time${uuid}`"
          :open-dropdown="toggleTimepicker"
          @input="update('time', 'picker', ...arguments)"
          @blur="$v.time.$touch()"
          :placeholder="placeholderTime"
        />
      </div>
      <div class="journey-date-select__validation">
        <ValidationMessage v-if="$v.date.$dirty && !$v.date.required">
          {{ `Please enter a ${placeholderDate}` }}
        </ValidationMessage>
        <ValidationMessage v-if="$v.time.$dirty && $v.time.$invalid">
          {{ `Please enter a ${placeholderTime}` }}
        </ValidationMessage>
        <ValidationMessage v-if="!$v.date.isValidDateTime">
          {{ `Please enter a time at least ${hoursAhead} hours from now` }}
        </ValidationMessage>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Vue from "vue";
import Vuelidate from "vuelidate";
import VuePikaday from "@enrian/vue-pikaday";
import { required } from "vuelidate/lib/validators";
import ValidationMessage from "../ValidationMessage";
import Tooltip from "../Tooltip/Tooltip";
import TimePicker from "../TimePicker/TimePicker";
import {
  isValidDateTime,
  isInvalidTimeFormat
} from "../SearchWidget/validation.js";

Vue.use(Vuelidate);
Vue.use(VuePikaday);

let uuid = 0;

export default {
  name: "JourneyDateSelect",
  components: {
    ValidationMessage,
    Tooltip,
    TimePicker
  },
  props: {
    value: {
      type: Date,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    tooltipText: {
      type: String,
      default: null
    },
    placeholderDate: {
      type: String,
      default: null
    },
    placeholderTime: {
      type: String,
      default: null
    },
    position: {
      type: Number,
      default: 0
    },
    pikadayOptions: {
      type: Object,
      default: value => {
        return Object.assign(
          {
            theme: "tgmag-pikaday-journey",
            minDate: new Date(),
            numberOfMonths: 2,
            format: "D MMM YYYY",
            blurFieldOnSelect: false
          },
          value
        );
      }
    },
    startRange: {
      type: Date,
      default: null
    },
    endRange: {
      type: Date,
      default: null
    },
    open: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    date: {
      immediate: true,
      handler(value, oldValue) {
        if (oldValue === value || value == null) return;
        this.update("date");
        if (!this.isTouch) {
          this.toggleTimepicker = true;
        }
      }
    },
    value: {
      immediate: true,
      handler(value, previous) {
        if (!value) {
          if (this.renderReady && this.pikaday) {
            this.clear();
          }
          return;
        }
        /*
         ** Use to debug pikaday and timezones
         */
        if (previous && moment(value).isSame(previous, "minute")) return;
        this.time = moment(value).format("HH:mm");
        this.date = moment(value).toDate();
      }
    },
    dateTime: {
      handler(value, previous) {
        if (moment(value).isSame(previous, "minute")) {
          return;
        }
        if (this.time && moment(value).isBefore(this.minDateTime)) {
          this.time = moment(this.minDateTime).format("HH:mm");
          this.dateTime = new Date(this.minDateTime);
        }
        let to = setTimeout(() => {
          clearTimeout(to);
          this.$emit("input", moment(this.dateTime).toDate());
        }, 0);
      }
    },
    startRange: {
      handler(value) {
        this.updateStartRange(value);
      }
    },
    endRange: {
      handler(value) {
        this.pikaday.setEndRange(new Date(value));
      }
    },
    open: {
      handler(value) {
        if (value) {
          this.pikaday.show();
          this.pikaday.gotoDate(this.startRange);
        } else {
          this.pikaday.hide();
        }
      }
    }
  },
  mounted() {
    this.renderReady = true;
    if (this.position) {
      setTimeout(() => {
        this.updateStartRange(this.startRange);
      }, 20);
    }
  },
  created() {
    this.uuid = uuid.toString();
    uuid += 1;
  },
  destroyed() {
    uuid -= 1;
  },
  data() {
    return {
      uuid: 0,
      date: null,
      time: null,
      dateTime: null,
      minDateTime: null,
      renderReady: false,
      pickerActive: false,
      showInput: "time",
      optionsTime: {
        time: true,
        delimiter: ":",
        timePattern: ["h", "m"]
      },
      hoursAhead: 2,
      toggleTimepicker: false
    };
  },
  computed: {
    isTouch: {
      get() {
        return "ontouchstart" in window ||
          navigator.MaxTouchPoints > 0 ||
          navigator.msMaxTouchPoints > 0
          ? true
          : false;
      },
      set(value) {
        return value;
      }
    },
    pikaday() {
      return this.$refs.pikaday.pikaday;
    },
    labels() {
      return {
        date: `${this.label} date`.trim(),
        time: `${this.label} time`.trim()
      };
    },
    minTime() {
      if (
        this.dateTime &&
        moment(this.dateTime).isSame(this.minDateTime, "day")
      ) {
        return moment(this.minDateTime).format("HH:mm");
      } else {
        return "00:00";
      }
    },
    allPikadayOptions() {
      const self = this;
      return Object.assign(this.pikadayOptions, {
        container: self.$parent.$refs.journeyDateContainer,
        onOpen: function() {
          setTimeout(function() {
            self.$emit("toggle", true);
          }, 100);
        },
        onClose: function() {
          self.$emit("toggle", false);
        }
      });
    }
  },
  methods: {
    setValidTime() {
      const increaseTime = moment()
        .add(this.hoursAhead, "hours")
        .format("HH:mm");
      this.time = moment(this.date).isSame(moment(), "day")
        ? increaseTime
        : null;
    },
    update(input, type, event) {
      if (input === "time" && type === "picker" && !event.includes("null")) {
        this.time = event;
      }
      /*
       ** IE11 cond: to check a valid date object is passed
       */
      if (
        Object.prototype.toString.call(this.date) != "[object Date]" ||
        !this.date
      )
        return;
      /*
       ** If we are using console.log(event); input then manually insert ':'into time value
       */
      if (this.time && !/\d{1,2}:\d{2}/.test(this.time)) {
        this.time = this.time.match(/.{1,2}/g).join(":");
      }
      if (!/\d{1,2}:\d{2}/.test(this.time) || !this.time) {
        /*
         ** When a time is not valid or empty, add default value.
         */
        this.setValidTime();
      } else if (/\d{1,2}:\d{2}/.test(this.time)) {
        /*
         ** Valid time
         */
        let timeParts = this.time.split(":").map(v => parseInt(v));
        let result = moment(this.date)
          .set({
            hour: timeParts[0],
            minute: timeParts[1]
          })
          .toDate();
        this.dateTime = result;
      }
    },
    clear() {
      this.pikaday.setDate(null);
    },
    updateStartRange(value) {
      this.pikaday.setStartRange(new Date(value));
      if (this.position != 0) {
        /*
         ** if this datepicker isn't the origin then update the min date to the start dates only.
         */
        this.minDateTime = value;
        this.pikaday.setMinDate(new Date(value));
      }
    }
  },
  validations: {
    date: {
      required,
      isValidDateTime
    },
    time: {
      required,
      isInvalidTimeFormat
    }
  }
};
</script>

<style lang="scss">
@import "./vue-pikaday.css";
@import "./pika-overrides.scss";
</style>
<style lang="scss" scoped>
.journey-date-select {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
}

.journey-date-select__label {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: baseline;
  margin-bottom: 10px;
  padding: 4px;
  font-weight: bold;
  font-size: 15px;
  color: $primary;
}

.journey-date-select__input {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 45%;
}

.journey-date-select__input--date {
  margin-right: 8px;
}

.journey-date-select__input--time {
  margin-left: 8px;
  min-width: 120px;
}

.journey-date-select__input label {
  position: absolute;
  z-index: 2;
  top: -13px;
  left: 10px;
  padding: 4px;
  font-weight: 600;
  font-size: 14px;
  margin: 0;
  color: #032f61;
}

.journey-date-select__input input {
  padding: 12px 18px;
  flex-grow: 1;
  max-width: 100%;
  font-size: 14px;
  font-weight: 400;
  // border: 1px solid #b8b8b8;
  border-radius: 4px;
  height: 56px;
  box-sizing: border-box;
  -webkit-appearance: none;
  background-color: white;
}
.journey-date-select__validation {
  position: relative;
  flex: 0 100%;
  width: 100%;
}
</style>
